(function () {
  const LOG_PREFIX = '[custom.js][variant]';

  const parseJsonFromScript = (scriptEl) => {
    if (!scriptEl) return null;
    try {
      const raw = scriptEl.textContent || scriptEl.innerText || '';
      return raw.trim() ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn(LOG_PREFIX, 'Failed to parse JSON', err);
      return null;
    }
  };

  const isPlainObject = (value) => {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  };

  const isJsonErrorObject = (value) => {
    return isPlainObject(value) && typeof value.error === 'string';
  };

  const pickFields = (obj, keys) => {
    if (!obj) return null;
    return keys.reduce((acc, key) => {
      if (obj[key] !== undefined) acc[key] = obj[key];
      return acc;
    }, {});
  };

  const escapeHtml = (value) => {
    const str = value == null ? '' : String(value);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const normalizeSpecsRows = (specs) => {
    if (!Array.isArray(specs)) return [];
    return specs
      .filter((row) => row && typeof row === 'object')
      .map((row) => ({
        key: row.key == null ? '' : String(row.key),
        value: row.value == null ? '' : String(row.value),
      }))
      .filter((row) => row.key !== '' || row.value !== '');
  };

  const parseSpecsValue = (value) => {
    if (Array.isArray(value)) return normalizeSpecsRows(value);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeSpecsRows(parsed);
      } catch (err) {
        return [];
      }
    }
    return [];
  };

  const buildSpecsTableHtml = (specRows) => {
    if (!Array.isArray(specRows) || specRows.length === 0) return '';
    const rowsHtml = specRows
      .map((row) => {
        return `<tr><th scope="row">${escapeHtml(row.key)}</th><td>${escapeHtml(row.value)}</td></tr>`;
      })
      .join('');

    return `<div class="table-container--mobile-scrollable"><table class="table"><tbody>${rowsHtml}</tbody></table></div>`;
  };

  const updateVariantSpecsTables = ({ productInfoEl, currentVariantMeta }) => {
    if (!productInfoEl) return;
    const containers = productInfoEl.querySelectorAll?.(
      '[data-technical-specs-container][data-technical-specs-source="variant"]'
    );
    if (!containers || containers.length === 0) return;

    if (!currentVariantMeta) {
      containers.forEach((container) => {
        container.innerHTML = '';
      });
      return;
    }

    const metafields = currentVariantMeta?.metafields || null;
    const technicalSpecsSkuMetafieldOrValue = metafields?.technical_specs_sku ?? null;
    const rawSpecsValue =
      technicalSpecsSkuMetafieldOrValue &&
      typeof technicalSpecsSkuMetafieldOrValue === 'object' &&
      !Array.isArray(technicalSpecsSkuMetafieldOrValue)
        ? typeof technicalSpecsSkuMetafieldOrValue.error === 'string'
          ? null
          : 'value' in technicalSpecsSkuMetafieldOrValue
            ? technicalSpecsSkuMetafieldOrValue.value
            : technicalSpecsSkuMetafieldOrValue
        : technicalSpecsSkuMetafieldOrValue;

    if (
      rawSpecsValue == null ||
      (Array.isArray(rawSpecsValue) && rawSpecsValue.length === 0) ||
      (typeof rawSpecsValue === 'string' && rawSpecsValue.trim() === '')
    ) {
      containers.forEach((container) => {
        container.innerHTML = '';
      });
      return;
    }

    const specRows = parseSpecsValue(rawSpecsValue);
    const html = buildSpecsTableHtml(specRows);

    containers.forEach((container) => {
      container.innerHTML = html;
    });
  };

  const getProductInfoBySectionId = (sectionId, context = document) => {
    if (!sectionId) return null;
    return (
      context.querySelector?.(`product-info[data-section="${sectionId}"]`) ||
      context.querySelector?.(`product-info[data-original-section="${sectionId}"]`) ||
      null
    );
  };

  const isMainProductInfo = (productInfoEl) => {
    return typeof productInfoEl?.id === 'string' && productInfoEl.id.startsWith('MainProduct-');
  };

  const normalizeMetaObject = (maybeMetaObject, fallbackMetaObject) => {
    if (!maybeMetaObject || isJsonErrorObject(maybeMetaObject)) return fallbackMetaObject || null;
    if (isPlainObject(maybeMetaObject) && Object.keys(maybeMetaObject).length === 0) return fallbackMetaObject || null;
    return maybeMetaObject;
  };

  const getProductDataAndMeta = (context = document) => {
    const productData = parseJsonFromScript(context.querySelector?.('script[type="application/json"][data-product-json]'));
    const metaRaw = parseJsonFromScript(
      context.querySelector?.('script[type="application/json"][data-product-meta-json]')
    );

    const productMetaData = Array.isArray(metaRaw) ? metaRaw : [];

    const normalizedProductMetaData = productMetaData.map((item) => {
      const metafields = normalizeMetaObject(item?.metafields, item?.metafieldsFallback);
      const prodMeta = normalizeMetaObject(item?.prodMeta, item?.prodMetaFallback);

      return {
        ...item,
        metafields,
        prodMeta,
      };
    });

    const productMetaMap = normalizedProductMetaData.reduce((acc, item) => {
      if (item?.id == null) return acc;
      acc[String(item.id)] = item.prodMeta;
      return acc;
    }, {});

    return {
      productData,
      productMetaData: normalizedProductMetaData,
      productMetaMap,
    };
  };

  const updateProductContextCache = (cacheKey, context) => {
    if (!cacheKey) return null;
    const data = getProductDataAndMeta(context);

    if (!data.productData && (!data.productMetaData || data.productMetaData.length === 0)) return null;

    window.ProductContextCache = window.ProductContextCache || {};
    window.ProductContextCache[cacheKey] = data;
    return data;
  };

  const logVariantChange = (payload) => {
    const data = payload?.data || null;
    if (!data) return;

    const sectionId = data.sectionId || null;
    const variant = data.variant || null;

    const htmlDoc = data.html || null;
    const productInfoFromHtml = htmlDoc ? getProductInfoBySectionId(sectionId, htmlDoc) : null;
    const productInfoFromDoc = getProductInfoBySectionId(sectionId, document);
    const context = productInfoFromHtml || productInfoFromDoc || htmlDoc || document;

    const mainProductInfoEl = isMainProductInfo(productInfoFromHtml)
      ? productInfoFromHtml
      : isMainProductInfo(productInfoFromDoc)
        ? productInfoFromDoc
        : null;

    const cacheKey = sectionId ? String(sectionId) : 'main';
    const cachedData =
      updateProductContextCache(cacheKey, context) || window.ProductContextCache?.[cacheKey] || getProductDataAndMeta(context);

    if (mainProductInfoEl) updateProductContextCache('main', mainProductInfoEl);

    const productData = cachedData?.productData || null;
    const productMetaData = cachedData?.productMetaData || [];
    const productMetaMap = cachedData?.productMetaMap || {};

    const variantId = variant?.id != null ? String(variant.id) : null;

    const currentVariantFromProductJson =
      productData?.variants && Array.isArray(productData.variants) && variantId
        ? productData.variants.find((v) => String(v?.id) === variantId) || null
        : null;

    const currentVariantMeta =
      variantId && Array.isArray(productMetaData)
        ? productMetaData.find((item) => String(item?.id) === variantId) || null
        : null;

    if (productInfoFromDoc) updateVariantSpecsTables({ productInfoEl: productInfoFromDoc, currentVariantMeta });

    const currentProductMeta = variantId ? productMetaMap[variantId] || null : null;

    const allVariantsCustomMetafieldsById = Array.isArray(productMetaData)
      ? productMetaData.reduce((acc, item) => {
          if (item?.id == null) return acc;
          acc[String(item.id)] = item?.metafields || null;
          return acc;
        }, {})
      : {};

    const groupTitleParts = [];
    if (productData?.handle) groupTitleParts.push(productData.handle);
    if (variantId) groupTitleParts.push(`variant ${variantId}`);
    const groupTitle = groupTitleParts.length ? `${LOG_PREFIX} ${groupTitleParts.join(' | ')}` : LOG_PREFIX;

    console.groupCollapsed(groupTitle);
    console.log(
      'Product',
      pickFields(productData, ['id', 'handle', 'title'])
    );
    console.log('Variant', {
      id: variant?.id ?? currentVariantFromProductJson?.id ?? null,
      title: variant?.title ?? currentVariantFromProductJson?.title ?? null,
      option1: variant?.option1 ?? currentVariantFromProductJson?.option1 ?? null,
      option2: variant?.option2 ?? currentVariantFromProductJson?.option2 ?? null,
      option3: variant?.option3 ?? currentVariantFromProductJson?.option3 ?? null,
      sku: variant?.sku ?? currentVariantFromProductJson?.sku ?? null,
      available: variant?.available ?? currentVariantFromProductJson?.available ?? null,
      price: variant?.price ?? currentVariantFromProductJson?.price ?? null,
      compare_at_price: variant?.compare_at_price ?? currentVariantFromProductJson?.compare_at_price ?? null,
    });
    console.log('Metafields (custom)', {
      product: currentProductMeta,
      allVariants: allVariantsCustomMetafieldsById,
      currentVariant: currentVariantMeta?.metafields || null,
    });
    console.groupEnd();
  };

  const init = () => {
    window.ProductContextCache = window.ProductContextCache || {};

    document.querySelectorAll('product-info').forEach((productInfoEl) => {
      const key = productInfoEl.dataset.section || productInfoEl.dataset.originalSection || productInfoEl.id || null;
      if (!key) return;
      const cachedData = updateProductContextCache(String(key), productInfoEl);
      const selectedVariant = parseJsonFromScript(
        productInfoEl.querySelector?.('variant-selects script[type="application/json"][data-selected-variant]')
      );
      const variantId = selectedVariant?.id != null ? String(selectedVariant.id) : null;
      const currentVariantMeta =
        variantId && cachedData?.productMetaData && Array.isArray(cachedData.productMetaData)
          ? cachedData.productMetaData.find((item) => String(item?.id) === variantId) || null
          : null;

      updateVariantSpecsTables({ productInfoEl, currentVariantMeta });
    });

    const mainProductInfoEl = document.querySelector('product-info[id^="MainProduct-"]');
    if (mainProductInfoEl) updateProductContextCache('main', mainProductInfoEl);

    if (!window.FoxTheme?.pubsub?.subscribe) return;
    if (!window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) return;

    window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, logVariantChange);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
